import { whenRefAvailable } from "@/utilities";
import type { CropperCanvas, CropperImage, CropperSelection } from "cropperjs";
import Cropper from "cropperjs";
import type { Ref } from "vue";
import { readonly } from "vue";
import type { Selection } from "@cropper/element-selection";

type Options = {
  container: Ref<HTMLDivElement | null>;
  template: string;
  onSelectionChange?: () => void;
  /** Minimum selection size as a ratio of canvas size (0–1). Defaults to 0.15. */
  minSizeRatio?: number;
  /** Initial selection size as a ratio of the maximum fit within the image (0–1). Defaults to 1.0. */
  initialCoverage?: number;
};

export type FinalImageSettings = {
  width?: number;
  height?: number;
};

export type FinalImageCreationCallback = (img: File) => void;

export function useImageEditor(options: Options) {
  const editor = ref<Cropper>();

  const create = async (img: HTMLImageElement) => {
    return whenRefAvailable(options.container).then((c) => {
      editor.value = new Cropper(img, {
        container: c.value,
        template: options.template,
      });

      getImage().$ready(() => {
        setInitialSelectionPosition();
        getSelection().addEventListener("change", (e) => preventSelectionOverflowOnSelectionChange(e as CustomEvent));
        if (options.onSelectionChange) {
          getSelection().addEventListener("change", options.onSelectionChange);
        }
      });
    });
  };

  const destroy = () => {
    editor.value?.destroy();
    editor.value = undefined;
  };

  const createFinalImage = async (options: FinalImageSettings = {}, callback: FinalImageCreationCallback) => {
    const selection = getSelection();
    const canvas = await selection.$toCanvas(options);
    canvas.toBlob((blob) => {
      if (blob === null) {
        throw new Error("Failed to get blob image data.");
      }
      const img = new File([blob], "image", { type: blob.type });
      callback(img);
    });
  };

  const getSelection = (): CropperSelection => {
    assertEditorCreated(editor.value);
    const selection = editor.value.getCropperSelection();
    if (selection === null) {
      throw new Error("Failed to get cropper selection.");
    } else {
      return selection;
    }
  };

  const getCanvas = (): CropperCanvas => {
    assertEditorCreated(editor.value);
    const canvas = editor.value.getCropperCanvas();
    if (canvas === null) {
      throw new Error("Failed to get cropper canvas.");
    } else {
      return canvas;
    }
  };

  const getImage = (): CropperImage => {
    assertEditorCreated(editor.value);
    const image = editor.value.getCropperImage();
    if (image === null) {
      throw new Error("Failed to get cropper image.");
    } else {
      return image;
    }
  };

  const preventSelectionOverflowOnSelectionChange = (event: CustomEvent) => {
    const selection = event.detail as Selection;

    const canvasRect = getCanvas().getBoundingClientRect();

    const maxSelection: Selection = {
      x: 0,
      y: 0,
      width: canvasRect.width,
      height: canvasRect.height,
    };

    // Prevent selection from leaving the image
    const isInside = isInSelection(selection, maxSelection);
    if (!isInside) {
      event.preventDefault();
      return;
    }

    // Prevent selection from being shrunk below the minimum size
    const minRatio = options.minSizeRatio ?? 0.15;
    const isTooSmall = selection.width < canvasRect.width * minRatio || selection.height < canvasRect.height * minRatio;
    if (isTooSmall) {
      event.preventDefault();
    }
  };

  const preventSelectionOverflowOnImageTransform = (event: CustomEvent) => {
    const canvas = getCanvas();
    const canvasRect = canvas.getBoundingClientRect();
    const imageClone = getImage().cloneNode() as CropperImage;

    imageClone.style.transform = `matrix(${event.detail.matrix.join(", ")})`;
    imageClone.style.opacity = "0";
    canvas.appendChild(imageClone);
    const imageRect = imageClone.getBoundingClientRect();
    canvas.removeChild(imageClone);

    const selection = getSelection();

    const maxSelection: Selection = {
      x: imageRect.left - canvasRect.left,
      y: imageRect.top - canvasRect.top,
      width: imageRect.width,
      height: imageRect.height,
    };

    const isInside = isInSelection(selection, maxSelection);
    if (!isInside) {
      event.preventDefault();
    }
  };

  const setInitialSelectionPosition = () => {
    const canvasRect = getCanvas().getBoundingClientRect();
    const imageRect = getImage().getBoundingClientRect();

    const selection = getSelection();
    const ratio = selection.aspectRatio;
    const coverage = options.initialCoverage ?? 1.0;

    let width, height;

    if (imageRect.height > imageRect.width) {
      width = imageRect.width * coverage;
      height = width / ratio;
    } else {
      height = imageRect.height * coverage;
      width = height * ratio;
    }

    const left = imageRect.left - canvasRect.left + (imageRect.width - width) / 2;
    const top = imageRect.top - canvasRect.top + (imageRect.height - height) / 2;

    selection.$change(left, top, width, height);
  };

  return {
    // editor instance
    editor: readonly(editor),
    create,
    destroy,

    // editor elements getters
    getSelection,
    getCanvas,
    getImage,

    // edited image
    createFinalImage,

    // helpers
    preventSelectionOverflowOnImageTransform,
    preventSelectionOverflowOnSelectionChange,
  };
}

function assertEditorCreated(editor: Cropper | undefined): asserts editor is Cropper {
  if (editor === undefined) {
    throw new Error("Editor is not created.");
  }
}

function isInSelection(selection: Selection, maxSelection: Selection): boolean {
  return (
    selection.x >= maxSelection.x && //
    selection.y >= maxSelection.y &&
    selection.x + selection.width <= maxSelection.x + maxSelection.width &&
    selection.y + selection.height <= maxSelection.y + maxSelection.height
  );
}
